import { StatusCard } from '@common/StatusCardWithBorder';
import { useMaintenanceNotificationUpdates } from '@pages/Maintenance/util';
import { XCircleIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Alert, Badges, Button, Ellipsis, Separator, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { getHourByTimezone } from '@prophecy/utils/date';
import { useTimedFlag } from '@prophecy/utils/react/hooks';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';
import { uniqueID } from '@prophecy/utils/uniqueID';
import { startCase } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import styled from 'styled-components';
import { format as timeAgoFormat } from 'timeago.js';

import { ScheduledMaintenanceKind, ScheduledMaintenanceType, ScheduleStatusTypes } from '../../context/types';

const AlertWrapper = styled.div`
  position: fixed;
  bottom: 60px;
  left: 0;
  z-index: ${theme.zLayer.m};
  display: flex;
  justify-content: center;
  user-select: none;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;

const StyledStackContainer = styled(Stack)`
  width: 100%;
  padding: ${theme.spaces.x14} 0;
  overflow-x: auto;
  overflow-y: hidden;
`;

const StyledStackItem = styled(StackItem)`
  max-width: 200px;
`;
const StyledSeparator = styled(Separator)`
  border-color: ${theme.colors.warning300};
  width: 100%;
`;

const ALERT_WIDTH = 500;

const StyledAlert = styled(Alert)`
  background: ${theme.colors.warning50};
  width: ${ALERT_WIDTH}px;
  padding: 0;
  border: 1px solid ${theme.colors.warning300};
`;

const MaintenanceInfo = ({ info = {} as ScheduledMaintenanceType }: { info: ScheduledMaintenanceType }) => {
  const currentStatus = info.status;
  const isError =
    currentStatus === ScheduleStatusTypes.error ||
    currentStatus === ScheduleStatusTypes.cancelled ||
    currentStatus === ScheduleStatusTypes.failed;
  let badgeTone = theme.colors.brand600 as string;
  let status = isCaseInsensitiveEqual(info.status, ScheduleStatusTypes.scheduled) ? 'Pending' : 'Underway';
  if (isError) {
    badgeTone = theme.colors.error600;
    status = currentStatus ?? 'Error';
  } else if (currentStatus === ScheduleStatusTypes.completed) {
    badgeTone = theme.colors.success600;
  }

  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x14}>
      <Stack direction='horizontal' gap={theme.spaces.x10} width='100%' alignY='center'>
        <StatusCard
          width='90'
          label='Status'
          hasSubTextTooltip={true}
          subText={startCase(status)}
          subTextTone={badgeTone}
        />
        <StatusCard
          label='Notice Posted At'
          width='120px'
          subText={timeAgoFormat(info.createdAt)}
          subTextTone={theme.colors.gray700}
        />
        <StatusCard label='Start Time' subText={timeAgoFormat(info.fromTime)} subTextTone={theme.colors.gray700} />
        <StatusCard label='End Time' subText={timeAgoFormat(info.toTime)} subTextTone={theme.colors.gray700} />
      </Stack>

      {info.customMessage && (
        <Stack width='450px'>
          <Ellipsis tooltip={true}>
            <Text level='sm' tone={theme.colors.gray700}>
              {info.customMessage}
            </Text>
          </Ellipsis>
        </Stack>
      )}
    </Stack>
  );
};

export function MaintenanceNotification() {
  const scheduledUpdateInfo = useMaintenanceNotificationUpdates();

  const [snoozeNotice, toggleSnooze] = useTimedFlag(30 * 60 * 1000);

  const [activeWindowMap, setActiveWindowMap] = useState<{ [key: number]: boolean }>({});
  const [activeTab, setActiveTab] = useState(0);
  const [hasDetailedInfo, toggleDetailedInfo] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const activeScheduledInfo = useMemo(
    () => (scheduledUpdateInfo ?? []).filter((info) => info.showBanner),
    [scheduledUpdateInfo]
  );

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeScheduledInfo.length > 0) {
      let activeMap: { [key: number]: boolean } = {};
      activeScheduledInfo.forEach((info, i) => {
        const createdAt = info.createdAt;
        const createdAtHours = getHourByTimezone(info.timezone, createdAt);
        const toTimeHours = getHourByTimezone(info.timezone, info.toTime);
        if (createdAtHours <= toTimeHours) {
          activeMap[i] = true;
        }
      });
      setActiveTab(activeScheduledInfo.length - 1);
      setActiveWindowMap(activeMap);
    }
  }, [activeScheduledInfo, setActiveWindowMap]);
  const filteredScheduledInfo = useMemo(
    () => activeScheduledInfo.filter((_, i) => activeWindowMap[i]),
    [activeWindowMap, activeScheduledInfo]
  );

  let uniqueTitles: string[] = [
    startCase(`${ScheduledMaintenanceKind.maintenance}0`),
    startCase(`${ScheduledMaintenanceKind.upgrade}0`),
    startCase(`${ScheduledMaintenanceKind.feature_enablement}0`)
  ];
  if (filteredScheduledInfo.length <= 0 || snoozeNotice) {
    return null;
  }

  const handleDrag = () => {
    setIsDragging(true);
  };

  const handleStop = () => {
    // Add a small delay to ensure the click event has finished propagating
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  return (
    <Draggable
      // css solution did not work - to center the alert in the screen
      defaultPosition={{ x: window.innerWidth / 2 - ALERT_WIDTH / 2, y: 0 }}
      allowAnyClick={true}
      axis='both'
      bounds='#root'
      onDrag={handleDrag}
      onStop={handleStop}>
      <AlertWrapper ref={ref}>
        <StyledAlert showIcon={false} key={'maintenanceNotification'} closable={false}>
          <Stack width='100%' gap={theme.spaces.x16} height='100%' alignY='center'>
            <Stack
              padding={hasDetailedInfo ? `${theme.spaces.x16} ${theme.spaces.x16} 0` : theme.spaces.x16}
              onClick={(e) => {
                if (isDragging) {
                  // Prevent the click event if it was part of a drag operation
                  e.stopPropagation();
                  return;
                }
                //Reset drag position on close behavior
                toggleDetailedInfo(!hasDetailedInfo);
              }}
              direction='horizontal'
              width='100%'
              alignY='center'
              gap={theme.spaces.x12}
              align='space-between'>
              <Text level='md' weight={theme.fontWeight.semiBold}>
                Update Starting Soon
              </Text>
              <Button
                aria-label='close-window'
                data-test-id='close-widnow'
                variant='plain'
                onClick={() => toggleSnooze()}
                icon={<XCircleIcon type='default' />}
              />
            </Stack>
            {hasDetailedInfo && (
              <>
                <StyledSeparator />
                <Stack width='100%' padding={` 0 ${theme.spaces.x16} ${theme.spaces.x16}`} gap={theme.spaces.x14}>
                  <Text level='sm' weight={theme.fontWeight.medium}>
                    Prophecy is about to receive an update. Please save your changes so you do not lose any work during
                    the update process.
                  </Text>
                  <StyledStackContainer direction='horizontal' width='100%' alignY='center' gap={theme.spaces.x12}>
                    {filteredScheduledInfo.map((info, i) => {
                      const name = uniqueID(startCase(info.kind), uniqueTitles);
                      uniqueTitles.push(name);
                      return (
                        <Badges
                          data-test-id='active-maintenancebadge'
                          onClick={() => setActiveTab(i)}
                          key={i + 'info'}
                          tone='brand'
                          variant={i === activeTab ? 'badgeColor' : 'badgeModern'}>
                          <StyledStackItem>
                            <Ellipsis tooltip={true}>{name}</Ellipsis>
                          </StyledStackItem>
                        </Badges>
                      );
                    })}
                  </StyledStackContainer>
                  <MaintenanceInfo info={filteredScheduledInfo[activeTab]} />
                </Stack>
              </>
            )}
          </Stack>
        </StyledAlert>
      </AlertWrapper>
    </Draggable>
  );
}
